import React, { useMemo } from 'react'
import isEqual from 'react-fast-compare'

import {
  useZoom,
  WebRendererDocument,
  WebRendererArtboard,
} from '@sketch-hq/sketch-web-renderer'

import * as Styled from './ArtboardsInfoOverlay.styled'
import { touchableClick } from '../../utils'

// Hide the artboard name if the artboard's width is less than:
const HEADER_MIN_WIDTH = 100

// Hide the cta text if the artboard's width is less than:
const CTA_TEXT_MIN_WIDTH = 160

type MouseEventCallback = (
  artboard: WebRendererArtboard,
  e: React.MouseEvent<HTMLElement> | React.TouchEvent<HTMLElement>
) => void

type TouchableClickEvent<E extends HTMLElement> =
  | React.MouseEvent<E>
  | React.TouchEvent<E>

export interface ArtboardProps {
  document: WebRendererDocument
  artboard: WebRendererArtboard
  selected?: boolean
  onClick?: MouseEventCallback
  onDoubleClick?: MouseEventCallback
  onCTAClick?: MouseEventCallback
  disablePointerCursor?: boolean
}

/**
 * This element takes care of:
 * - Rendering the artboard name, cta and artboard outline.
 * - Tracking the user events on the artboard and emit them when needed.
 */
export const Artboard = React.forwardRef<HTMLDivElement, ArtboardProps>(
  (props, ref) => {
    const {
      document,
      artboard,
      selected,
      onClick,
      onDoubleClick,
      onCTAClick,
      disablePointerCursor,
    } = props

    const zoom = useZoom() ?? 1

    // The real width, taking into consideration the zoom level
    const widthOnScreen = artboard.bounds.width * zoom
    const showHeader = widthOnScreen > HEADER_MIN_WIDTH
    const showCTAText = widthOnScreen > CTA_TEXT_MIN_WIDTH

    // These values are all percentages, so we don't need
    // to update them in case the user zooms the canvas
    const { x, y, width, height } = useMemo(() => {
      const bounds = artboard.bounds
      const docBounds = document.bounds

      return {
        x: (bounds.x / docBounds.width) * 100,
        y: (bounds.y / docBounds.height) * 100,
        width: (bounds.width / docBounds.width) * 100,
        height: (bounds.height / docBounds.height) * 100,
      }
    }, [artboard.bounds, document.bounds])

    const handleDoubleClick = (e: React.MouseEvent<HTMLDivElement>) => {
      onDoubleClick?.(artboard, e)
    }

    const touchableClickEvents = useMemo(
      () => ({
        container: touchableClick((e: TouchableClickEvent<HTMLDivElement>) => {
          onClick?.(artboard, e)
        }),
        cta: touchableClick((e: TouchableClickEvent<HTMLButtonElement>) => {
          onCTAClick?.(artboard, e)
        }),
      }),
      [artboard, onCTAClick, onClick]
    )

    return (
      <Styled.Artboard
        ref={ref}
        aria-selected={selected}
        data-testid="artboard-overlay"
        data-artboard-uuid={artboard.objectId}
        {...touchableClickEvents.container}
        onDoubleClick={handleDoubleClick}
        style={{
          left: `${x}%`,
          top: `${y}%`,
          width: `${width}%`,
          height: `${height}%`,
          cursor: disablePointerCursor ? 'inherit' : undefined,
        }}
      >
        <Styled.ArtboardHeader
          style={{ visibility: showHeader ? undefined : 'hidden' }}
        >
          <Styled.ArtboardName>{artboard.name}</Styled.ArtboardName>
          <Styled.ArtboardCTA {...touchableClickEvents.cta}>
            <Styled.ArtboardCTAText
              style={{ display: showCTAText ? undefined : 'none' }}
            >
              View details
            </Styled.ArtboardCTAText>
            <Styled.ArrowIconWrapper>
              <Styled.ArrowIcon />
            </Styled.ArrowIconWrapper>
          </Styled.ArtboardCTA>
        </Styled.ArtboardHeader>
      </Styled.Artboard>
    )
  }
)

Artboard.displayName = 'Artboard'

export default React.memo(Artboard, isEqual)
